import MDInput from "components/MDInput";
import { ComponentProps } from "react";
import { draftColors, draftTypography } from "modules/proposals/draft/draftDesignTokens";

type MDInputProps = ComponentProps<typeof MDInput>;

type UiInputSize = "compact" | "default";

type UiInputProps = MDInputProps & {
  uiSize?: UiInputSize;
  textAlign?: "left" | "right";
  fontWeight?: number;
  readOnly?: boolean;
};

const compactSx = (align: "left" | "right", weight: number) =>
  ({
    display: "flex",
    flexDirection: "column",
    "& .MuiInputBase-root": {
      height: "24px",
      borderRadius: "6px",
      backgroundColor: draftColors.surface,
    },
    "& .MuiInputBase-input": {
      py: 0,
      px: "6px",
      fontSize: draftTypography.body12.fontSize,
      fontWeight: weight,
      lineHeight: draftTypography.body12.lineHeight,
      textAlign: align,
    },
  } as const);

const readOnlySx = {
  "& .MuiInputBase-root.MuiInputBase-root": {
    backgroundColor: "#f0f2f5",
    pointerEvents: "none",
  },
} as const;

export default function UiInput({
  uiSize = "compact",
  textAlign = "left",
  fontWeight = draftTypography.body12.fontWeight,
  readOnly,
  sx,
  inputProps: inputPropsProp,
  value,
  ...rest
}: UiInputProps) {
  const safeValue = typeof value === "number" && isNaN(value) ? "" : value;
  const mergedInputProps = {
    ...inputPropsProp,
    ...(readOnly && { readOnly: true, tabIndex: -1 }),
  };

  const readOnlyStyles = readOnly ? readOnlySx : {};

  if (uiSize === "default") {
    return (
      <MDInput
        sx={{ ...readOnlyStyles, ...sx }}
        inputProps={mergedInputProps}
        {...rest}
        value={safeValue}
      />
    );
  }

  return (
    <MDInput
      sx={{ ...compactSx(textAlign, fontWeight), ...readOnlyStyles, ...sx }}
      inputProps={mergedInputProps}
      {...rest}
      value={safeValue}
    />
  );
}
